import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaTrash } from "react-icons/fa";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import ProductNavbar from "./ProductNavbar";

function ImageDropzone({ file, onFileChange, onRemove, isFeatured }) {
  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      // 📹 Handle file rejections from the useDropzone configuration hook limits
      if (fileRejections.length > 0) {
        const errorMsg = fileRejections[0].errors[0].code === "file-too-large" 
          ? "File is too large! Maximum limit is 15 MB." 
          : "Invalid file type.";
          
        return Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: errorMsg,
        });
      }

      if (!acceptedFiles.length) return;
      
      const newFile = Object.assign(acceptedFiles[0], {
        preview: URL.createObjectURL(acceptedFiles[0]),
      });
      onFileChange(newFile);
    },
    [onFileChange]
  );

  // 📹 Configured to accept both Images and Videos up to 15MB max
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      "image/*": [],
      "video/*": [] 
    },
    multiple: false,
    maxSize: 15 * 1024 * 1024, // 15 Megabytes in bytes
  });

  const handleRemove = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This file will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        onRemove(file);
      }
    });
  };

  // Helper function to check if the file object or url represents a video stream
  const isVideoFile = (fileObj) => {
    if (fileObj.type && fileObj.type.startsWith("video/")) return true;
    const path = fileObj.image_path || "";
    return /\.(mp4|webm|ogg|mov|m4v)$/i.test(path);
  };

  return (
    <div
      {...getRootProps()}
      className="border border-dashed d-flex align-items-center justify-content-center"
      style={{
        width: "160px",
        height: "160px",
        borderRadius: "6px",
        position: "relative",
        cursor: "pointer",
        borderColor: isFeatured ? "#28a745" : "#ccc",
      }}
    >
      <input {...getInputProps()} />
      {file ? (
        <>
          {/* 📹 Render appropriate view layer component depending on file type */}
          {isVideoFile(file) ? (
            <video
              src={file.preview || (file.image_path ? imgurl + file.image_path : "")}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
              muted
              playsInline
            />
          ) : (
            <img
              src={file.preview || (file.image_path ? imgurl + file.image_path : "")}
              alt="preview"
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }}
            />
          )}

          {isFeatured && (
            <span
              style={{
                position: "absolute",
                bottom: "2px",
                left: "2px",
                backgroundColor: "#28a745",
                color: "#fff",
                fontSize: "10px",
                padding: "1px 4px",
                borderRadius: "4px",
              }}
            >
              Featured
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              background: "#ff4d4f",
              border: "none",
              borderRadius: "50%",
              width: "22px",
              height: "22px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <FaTrash size={14} />
          </button>
        </>
      ) : (
        <p style={{ fontSize: "12px", textAlign: "center", padding: "10px" }}>
          {isDragActive ? "Drop here" : "Add Image or Video (Max 15MB)"}
        </p>
      )}
    </div>
  );
}

function ProductGallery() {
  const { uuid } = useParams();
  const MAX_IMAGES = 10;
  const [gallery, setGallery] = useState(Array(MAX_IMAGES).fill(null));

  // Fetch existing items on load
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await axios.get(`${baseurl}getProductGallery/${uuid}`);
        const images = res.data.images || [];
        const galleryArray = Array(MAX_IMAGES).fill(null);
        images.forEach((img, idx) => {
          galleryArray[idx] = img;
        });
        setGallery(galleryArray);
      } catch (err) {
        console.error("Error fetching gallery:", err);
      }
    };
    fetchImages();
  }, [uuid]);

  const handleGalleryChange = (index, file) => {
    const newGallery = [...gallery];
    newGallery[index] = file;
    setGallery(newGallery);
  };

  const handleGalleryRemove = async (file) => {
    if (file.uuid) {
      try {
        await axios.delete(`${baseurl}deleteProductGalleryImage/${file.uuid}`);
        setGallery(gallery.map((g) => (g && g.uuid === file.uuid ? null : g)));
      } catch (err) {
        console.error("Error deleting item:", err);
      }
    } else {
      setGallery(gallery.map((g) => (g === file ? null : g)));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("product_uuid", uuid);
    gallery.forEach((file) => {
      if (file && !file.uuid) formData.append("gallery", file);
    });

    try {
      const res = await axios.post(`${baseurl}addUpdateProductGallery/${uuid}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Gallery updated successfully!",
        showConfirmButton: false,
        timer: 2000,
        toast: true,
      });

      const updatedGallery = Array(MAX_IMAGES).fill(null);
      res.data.images.forEach((img, idx) => (updatedGallery[idx] = img));
      setGallery(updatedGallery);
    } catch (err) {
      console.error("Error uploading gallery data:", err);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Failed to upload files",
        showConfirmButton: false,
        timer: 2500,
        toast: true,
      });
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <ProductNavbar/>
          <div className="col-12 mb-2">
            <p style={{ fontSize: "12px", color: "#555" }}>
              Note: The first block is considered the <strong>Featured Media</strong>. Video uploads must be smaller than 15 MB.
            </p>
          </div>
          <div className="col-12 mb-4">
            <div className="card">
              <div className="card-header">Upload Product Media Gallery</div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {gallery.map((file, i) => (
                      <ImageDropzone
                        key={i}
                        file={file}
                        onFileChange={(f) => handleGalleryChange(i, f)}
                        onRemove={handleGalleryRemove}
                        isFeatured={i === 0 && file !== null}
                      />
                    ))}
                  </div>
                  <div className="text-end">
                    <button type="submit" className="btn btn-primary">
                      Upload Files
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ProductGallery;